/**
 * Helper condiviso per il submit dei form pubblici del sito istituzionale.
 *
 * Sostituisce le chiamate `axios.post()` dirette a contacti.kinsta.cloud
 * (Gravity Forms) e info.gefran.com (Pardot) — ora tutto passa per la Netlify
 * Function `/.netlify/functions/verify-form-submit` che:
 *  1. Verifica il token Cloudflare Turnstile
 *  2. Inoltra al backend appropriato (GF o Pardot) con Basic Auth quando serve
 *
 * Nessun URL backend o credenziale è più esposto al client.
 */

const FUNCTION_URL = '/.netlify/functions/verify-form-submit'

/**
 * Errore custom sollevato dalle submit helper. Porta un `code` machine-readable
 * per mappatura i18n e opzionalmente `codes` con i sub-code Turnstile.
 */
export class SubmitFormError extends Error {
  constructor(code, codes = [], meta = {}) {
    super(code)
    this.name = 'SubmitFormError'
    this.code = code
    this.codes = codes
    this.meta = meta
  }
}

async function callFunction(payload) {
  let res
  try {
    res = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch (e) {
    throw new SubmitFormError('network_error', [], { message: e.message })
  }

  const text = await res.text()
  let body
  try { body = text ? JSON.parse(text) : {} } catch { body = { raw: text } }

  if (!res.ok) {
    throw new SubmitFormError(body.error || `http_${res.status}`, body.codes || [], { status: res.status, body })
  }

  return body
}

/**
 * Submit di un form Gravity Forms.
 *
 * @param {object} args
 * @param {number} args.formId          formId Gravity Forms (int > 0)
 * @param {object} args.formData        payload GF (es. { input_1: "…", input_2: "…" })
 * @param {string} args.turnstileToken  token emesso dal widget Cloudflare Turnstile
 * @returns {Promise<object>}           risposta dal backend GF (di solito { is_valid: true, ... })
 * @throws {SubmitFormError}            se Turnstile fallisce o il backend risponde errore
 */
export async function submitGravityForm({ formId, formData, turnstileToken }) {
  return callFunction({
    formType: 'gravityforms',
    formId,
    formData,
    turnstileToken,
  })
}

/**
 * Submit di un form Pardot (Salesforce Marketing Cloud).
 *
 * @param {object} args
 * @param {string} args.pardotUrl       URL Pardot (deve matchare la whitelist server-side)
 * @param {object} args.formData        campi form (verranno form-encoded)
 * @param {string} args.turnstileToken  token emesso dal widget Cloudflare Turnstile
 * @returns {Promise<object>}           { success: true } se ok
 * @throws {SubmitFormError}            se Turnstile fallisce o il backend risponde errore
 */
export async function submitPardotForm({ pardotUrl, formData, turnstileToken }) {
  return callFunction({
    formType: 'pardot',
    pardotUrl,
    formData,
    turnstileToken,
  })
}
